/**
 * RTK Query API for chatbot backend integration
 *
 * Provides endpoints for creating sessions and sending messages.
 * Uses the shared baseQueryWithReauth so the chatbot benefits from the
 * centralized CSRF token injection and session-error retry logic that
 * the rest of the app uses. Previously this file used a separate
 * fetchBaseQuery instance that bypassed CSRF, which broke mutations
 * once the backend started requiring CSRF on chatbot endpoints.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../api/baseQuery';
import type { ChatSession, ChatMessage } from './chatbotSlice';

interface CreateSessionRequest {
  language: string;
}

interface SendMessageRequest {
  session_id: string;
  message: string;
  language: string;
}

interface SendMessageResponse {
  user_message: ChatMessage;
  assistant_message: ChatMessage;
  session: ChatSession;
}

interface ChatHealthResponse {
  status: string;
  embedding_service: boolean;
  vector_service: boolean;
  llm_service: boolean;
  total_documents: number;
  vector_db_path: string;
  llm_model: string;
}

export const chatbotApi = createApi({
  reducerPath: 'chatbotApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ChatSession', 'ChatMessage'],
  endpoints: builder => ({
    createSession: builder.mutation<ChatSession, CreateSessionRequest>({
      query: body => ({
        url: 'chatbot/sessions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChatSession'],
    }),

    sendMessage: builder.mutation<SendMessageResponse, SendMessageRequest>({
      query: body => ({
        url: 'chatbot/messages/send/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChatMessage'],
    }),

    getSessionHistory: builder.query<
      { session: ChatSession; messages: ChatMessage[] },
      string
    >({
      query: sessionId => `chatbot/sessions/${sessionId}/history/`,
      providesTags: ['ChatMessage'],
    }),

    getChatHealth: builder.query<ChatHealthResponse, void>({
      query: () => 'chatbot/health/',
    }),
  }),
});

export const {
  useCreateSessionMutation,
  useSendMessageMutation,
  useGetSessionHistoryQuery,
  useGetChatHealthQuery,
} = chatbotApi;
